/**
 * Copyright (c) Microsoft. All rights reserved.
 */

export const inBetween = (small: number, great: number) => (target: number) => great >= target && small <= target;

export const doLabelsOverlap = <T>(firstLabel: T, secondLabel: T, getLabelRange: (label: T) => { start: number; end: number }): boolean => {
    const fSIndex = getLabelRange(firstLabel).start;
    const fEIndex = getLabelRange(firstLabel).end;

    const sSIndex = getLabelRange(secondLabel).start;
    const sEIndex = getLabelRange(secondLabel).end;

    return (
        inBetween(sSIndex, sEIndex)(fSIndex) ||
        inBetween(sSIndex, sEIndex)(fEIndex) ||
        inBetween(fSIndex, fEIndex)(sSIndex) ||
        inBetween(fSIndex, fEIndex)(sEIndex)
    );
};

export const partiallyOverlap = <T>(labelA: T, labelB: T, config: { getStart: (label: T) => number; getEnd: (label: T) => number }) =>
    (config.getStart(labelA) >= config.getStart(labelB) && config.getStart(labelA) <= config.getEnd(labelB)) ||
    (config.getStart(labelB) >= config.getStart(labelA) && config.getStart(labelB) <= config.getEnd(labelA));

export const entityLabelPartiallyOverlap = <T extends { startTokenIndex: number; endTokenIndex: number }>(labelA: T, labelB: T) =>
    partiallyOverlap(labelA, labelB, {
        getStart: (label: T) => label.startTokenIndex,
        getEnd: (label: T) => label.endTokenIndex
    });

/**
 * Creates a reducer similar to React.useReducer.
 */
export const createReducer = <State, Action>(reducer: (state: State, action: Action) => State, defaultState: State) => {
    let state = defaultState;

    return {
        getState(): State {
            return state;
        },
        getDefaultState(): State {
            return defaultState;
        },
        dispatch(action: Action) {
            state = reducer(state, action);
        }
    };
};
